import { useState } from 'react';
import { difference } from 'lodash';

import { styled } from '../../../../../../stitches.config';
import { Button, Icon, Popover } from '../../../../..';

export type Value = string[];

export type Group = {
  name: string;
  displayName?: string;
  smarts: string[];
  image?: string;
  subgroups: Group[];
};

interface FunctionalGroupProps {
  group: Group;
  value: Value;
  onChange: (value: Value) => void;
  onOpen: () => void;
}

const Header = styled('div', {
  display: 'flex',
  gap: '$3',
  alignItems: 'center',
  padding: '$3',
  fontSize: '$2',
  color: '$gray11',
});

const Container = styled('div', {
  'borderRadius': '$small',
  'backgroundColor': '$gray2',
  'padding': '$3',
  '&:hover': {
    backgroundColor: '$gray3',
  },
  'variants': {
    hasSubgroups: {
      true: {
        cursor: 'pointer',
      },
    },
    included: {
      true: {
        boxShadow: '0 0 0 1px #A4BE7B',
        boxShadowColor: 'green !important',
      },
    },
  },
});

const ImageContainer = styled('div', {
  position: 'relative',
});

const ImageIconContainer = styled('div', {
  height: '100px',
  fontSize: '75px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export function FunctionalGroup({ group, value, onChange, onOpen }: FunctionalGroupProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hasSubgroups = group.subgroups.length > 0;

  // Represents elements that exist in group.smarts and don't exist in value [0], and the other way around [1]
  const inclusion = [difference(group.smarts, value), difference(value, group.smarts)];
  const isFullySelected = value.length > 0 && inclusion[0].length === 0;
  const isPartlySelected = value.length > 0 && !isFullySelected && inclusion[1].length === 0;
  const isNotSelected = !isFullySelected && !isPartlySelected;

  const imageId = group.image || group.smarts.length === 1 ? group.smarts[0] : undefined;
  const selectionCount = value.length - inclusion[1].length;

  let buttonText: string;
  if ((isFullySelected && hasSubgroups) || isPartlySelected) {
    buttonText = `${selectionCount} selected`;
  } else if (isFullySelected) {
    buttonText = 'Selected';
  } else {
    buttonText = 'Select';
  }

  function onCopyClick() {
    navigator.clipboard.writeText(group.smarts[0]);
    setIsTooltipOpen(true);
    setTimeout(() => setIsTooltipOpen(false), 1000);
  }

  function onCardClick() {
    if (hasSubgroups) onOpen();
  }

  function onSelectClick(event: any) {
    event.stopPropagation();
    onChange(group.smarts);
  }

  return (
    <Container hasSubgroups={hasSubgroups} included={isFullySelected} onClick={onCardClick}>
      <Header>
        {hasSubgroups ? <Icon icon="folder" /> : <Icon icon="polyline" />}
        {group.name}
      </Header>

      <ImageContainer>
        {group.smarts.length === 1 && (
          <div style={{ position: 'absolute', top: 3, right: 3 }}>
            <Popover
              isOpen={isTooltipOpen}
              trigger={<Button icon="content_copy" size="small" weight="minimal" onClick={onCopyClick} />}
              triggerProps={{ asChild: true }}
              contentProps={{ asChild: true, side: 'bottom' }}
            >
              Copied to clipboard!
            </Popover>
          </div>
        )}

        {imageId ? (
          <svg width="100%" height="100" viewBox="71.992 -534.698 416.356 300.393" style={{ verticalAlign: 'bottom' }}>
            <use xlinkHref={`/functional-groups.svg#${imageId}`} />
          </svg>
        ) : (
          <ImageIconContainer>
            <Icon icon="folder" />
          </ImageIconContainer>
        )}
      </ImageContainer>

      <Button
        size="small"
        theme={!isNotSelected ? 'green' : undefined}
        weight="minimal"
        onClick={onSelectClick}
        text={buttonText}
        icon={!isNotSelected ? 'done' : undefined}
      />
    </Container>
  );
}

FunctionalGroup.defaultProps = {
  selected: false,
};
